import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, GraduationCap, Brain, 
  Download, Loader2, AlertCircle, CheckCircle, Clock,
  ChevronUp, ChevronDown, Minus, School, BookOpen,
  FileText, Calendar, Target, Award
} from 'lucide-react';
import api from '../lib/api';

const RelatorioConsolidado = () => {
  const [loading, setLoading] = useState(true);
  const [turmas, setTurmas] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState('all');
  const [selectedTrimestre, setSelectedTrimestre] = useState('all');
  const [evolucaoData, setEvolucaoData] = useState(null);
  const [turmasStats, setTurmasStats] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (turmas.length > 0) {
      loadConsolidado();
    }
  }, [selectedTurma, selectedTrimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Carregar turmas para o filtro
      const turmasRes = await api.get('/class-groups');
      setTurmas(turmasRes.data || []);
      
      // Carregar dados consolidados
      await loadConsolidado();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidado = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTrimestre !== 'all') params.append('trimestre', selectedTrimestre);
      
      const res = await api.get(`/dna-ia/relatorio-consolidado?${params.toString()}`);
      const data = res.data;
      
      // Filtrar por turma se necessário
      let turmasFiltradas = data.turmas || [];
      if (selectedTurma !== 'all') {
        turmasFiltradas = turmasFiltradas.filter(t => t.turma_id === selectedTurma);
      }
      
      setTurmasStats(turmasFiltradas);
      
      // Se filtrado por turma, recalcular estatísticas gerais
      if (selectedTurma !== 'all' && turmasFiltradas.length > 0) {
        // Carregar evolução específica da turma
        await loadEvolucao();
      } else {
        setEvolucaoData(null);
      }
    } catch (error) {
      console.error('Erro ao carregar consolidado:', error);
      // Fallback para o método antigo
      await loadTurmasStats();
    }
  };

  const loadTurmasStats = async () => {
    try {
      // Carregar estatísticas de DNA.IA para cada turma
      const turmasRes = await api.get('/class-groups');
      const turmasList = turmasRes.data || [];
      
      const stats = [];
      for (const turma of turmasList.slice(0, 20)) { // Limitar para não sobrecarregar
        try {
          const res = await api.get(`/dna-ia/evolucao?turma_id=${turma.id}`);
          if (res.data && res.data.alunos && res.data.alunos.length > 0) {
            stats.push({
              turma_id: turma.id,
              turma_name: turma.name,
              ...res.data.estatisticas,
              alunos: res.data.alunos
            });
          }
        } catch (e) {
          // Ignorar erros de turmas sem dados
        }
      }
      
      setTurmasStats(stats);
    } catch (error) {
      console.error('Erro ao carregar stats das turmas:', error);
    }
  };

  const loadEvolucao = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTurma !== 'all') params.append('turma_id', selectedTurma);
      if (selectedTrimestre !== 'all') params.append('trimestre', selectedTrimestre);
      
      const res = await api.get(`/dna-ia/evolucao?${params.toString()}`);
      setEvolucaoData(res.data);
    } catch (error) {
      console.error('Erro ao carregar evolução:', error);
    }
  };

  const getNivelColor = (nivel) => {
    switch (nivel) {
      case 1: return 'bg-red-100 text-red-700';
      case 2: return 'bg-yellow-100 text-yellow-700';
      case 3: return 'bg-blue-100 text-blue-700';
      case 4: return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getNivelNome = (nivel) => {
    switch (nivel) {
      case 1: return 'Crítico';
      case 2: return 'Em Desenvolvimento';
      case 3: return 'Adequado';
      case 4: return 'Avançado';
      default: return 'N/A';
    }
  };

  // Calcular estatísticas gerais
  const statsGerais = {
    totalTurmas: turmasStats.length,
    totalAlunos: turmasStats.reduce((acc, t) => acc + (t.total_alunos || 0), 0),
    totalAvaliacoes: turmasStats.reduce((acc, t) => acc + (t.total_avaliacoes || 0), 0),
    mediaWCPM: turmasStats.length > 0 
      ? Math.round(turmasStats.reduce((acc, t) => acc + (t.media_wcpm_turma || 0), 0) / turmasStats.length)
      : 0,
    distribuicaoNiveis: {
      critico: turmasStats.reduce((acc, t) => acc + (t.niveis?.critico || 0), 0),
      em_desenvolvimento: turmasStats.reduce((acc, t) => acc + (t.niveis?.em_desenvolvimento || 0), 0),
      adequado: turmasStats.reduce((acc, t) => acc + (t.niveis?.adequado || 0), 0),
      avancado: turmasStats.reduce((acc, t) => acc + (t.niveis?.avancado || 0), 0),
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-pink-600 to-rose-600 rounded-xl">
            <BarChart3 className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Relatório Consolidado</h1>
            <p className="text-muted-foreground">Visão geral da evolução de fluência de leitura</p>
          </div>
        </div>
        <Button variant="outline" data-testid="exportar-relatorio-btn">
          <Download className="w-4 h-4 mr-2" />
          Exportar Relatório
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <School className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedTurma} onValueChange={setSelectedTurma}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Todas as turmas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Turmas</SelectItem>
                  {turmas.map(turma => (
                    <SelectItem key={turma.id} value={turma.id}>{turma.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedTrimestre} onValueChange={setSelectedTrimestre}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Todos os trimestres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Trimestres</SelectItem>
                  <SelectItem value="1">1º Trimestre (28/01 - 08/05)</SelectItem>
                  <SelectItem value="2">2º Trimestre (11/05 - 28/08)</SelectItem>
                  <SelectItem value="3">3º Trimestre (31/08 - 17/12)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" onClick={loadData} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              <span className="ml-2">Atualizar</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Estatísticas Gerais */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <School className="w-10 h-10 text-blue-600" />
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{statsGerais.totalTurmas}</p>
                    <p className="text-sm text-muted-foreground">Turmas Avaliadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Users className="w-10 h-10 text-purple-600" />
                  <div>
                    <p className="text-3xl font-bold text-purple-600">{statsGerais.totalAlunos}</p>
                    <p className="text-sm text-muted-foreground">Alunos Avaliados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-green-600" />
                  <div>
                    <p className="text-3xl font-bold text-green-600">{statsGerais.totalAvaliacoes}</p>
                    <p className="text-sm text-muted-foreground">Avaliações Realizadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Target className="w-10 h-10 text-orange-600" />
                  <div>
                    <p className="text-3xl font-bold text-orange-600">{statsGerais.mediaWCPM}</p>
                    <p className="text-sm text-muted-foreground">Média WCPM Geral</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-pink-50 to-rose-50">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Award className="w-6 h-6 text-pink-600" />
                    <span className="font-medium text-pink-700">Distribuição</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span>Crítico: {statsGerais.distribuicaoNiveis.critico}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <span>Desenv.: {statsGerais.distribuicaoNiveis.em_desenvolvimento}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span>Adequado: {statsGerais.distribuicaoNiveis.adequado}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span>Avançado: {statsGerais.distribuicaoNiveis.avancado}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs com diferentes visões */}
          <Tabs defaultValue="turmas" className="space-y-4">
            <TabsList>
              <TabsTrigger value="turmas" data-testid="tab-turmas">
                <School className="w-4 h-4 mr-2" />
                Por Turma
              </TabsTrigger>
              <TabsTrigger value="niveis" data-testid="tab-niveis">
                <BarChart3 className="w-4 h-4 mr-2" />
                Por Nível
              </TabsTrigger>
              <TabsTrigger value="evolucao" data-testid="tab-evolucao-geral">
                <TrendingUp className="w-4 h-4 mr-2" />
                Evolução Geral
              </TabsTrigger>
            </TabsList>

            {/* Tab Por Turma */}
            <TabsContent value="turmas">
              <Card>
                <CardHeader>
                  <CardTitle>Desempenho por Turma</CardTitle>
                  <CardDescription>Comparativo de fluência entre as turmas da escola</CardDescription>
                </CardHeader>
                <CardContent>
                  {turmasStats.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma turma com avaliações registradas</p>
                      <p className="text-sm mt-2">Registre avaliações no DNA.IA para ver os dados consolidados</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Turma</TableHead>
                          <TableHead className="text-center">Alunos</TableHead>
                          <TableHead className="text-center">Avaliações</TableHead>
                          <TableHead className="text-center">Média WCPM</TableHead>
                          <TableHead className="text-center">Evolução Média</TableHead>
                          <TableHead>Distribuição de Níveis</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {turmasStats.map(turma => (
                          <TableRow key={turma.turma_id}>
                            <TableCell className="font-medium">{turma.turma_name}</TableCell>
                            <TableCell className="text-center">{turma.total_alunos}</TableCell>
                            <TableCell className="text-center">{turma.total_avaliacoes}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="font-mono">
                                {turma.media_wcpm_turma || 0}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold flex items-center justify-center gap-1 ${
                                (turma.media_evolucao || 0) > 0 ? 'text-green-600' : 
                                (turma.media_evolucao || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {(turma.media_evolucao || 0) > 0 ? <ChevronUp className="w-4 h-4" /> : 
                                 (turma.media_evolucao || 0) < 0 ? <ChevronDown className="w-4 h-4" /> : 
                                 <Minus className="w-4 h-4" />}
                                {turma.media_evolucao > 0 ? '+' : ''}{turma.media_evolucao || 0}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                                  {turma.niveis?.critico > 0 && (
                                    <div 
                                      className="h-full bg-red-500" 
                                      style={{ width: `${(turma.niveis.critico / turma.total_alunos) * 100}%` }}
                                      title={`Crítico: ${turma.niveis.critico}`}
                                    />
                                  )}
                                  {turma.niveis?.em_desenvolvimento > 0 && (
                                    <div 
                                      className="h-full bg-yellow-500" 
                                      style={{ width: `${(turma.niveis.em_desenvolvimento / turma.total_alunos) * 100}%` }}
                                      title={`Em Desenvolvimento: ${turma.niveis.em_desenvolvimento}`}
                                    />
                                  )}
                                  {turma.niveis?.adequado > 0 && (
                                    <div 
                                      className="h-full bg-blue-500" 
                                      style={{ width: `${(turma.niveis.adequado / turma.total_alunos) * 100}%` }}
                                      title={`Adequado: ${turma.niveis.adequado}`}
                                    />
                                  )}
                                  {turma.niveis?.avancado > 0 && (
                                    <div 
                                      className="h-full bg-green-500" 
                                      style={{ width: `${(turma.niveis.avancado / turma.total_alunos) * 100}%` }}
                                      title={`Avançado: ${turma.niveis.avancado}`}
                                    />
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab Por Nível */}
            <TabsContent value="niveis">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { nivel: 1, nome: 'Crítico', cor: 'red', desc: '0-40 WCPM - Necessita intervenção intensiva', icon: AlertCircle },
                  { nivel: 2, nome: 'Em Desenvolvimento', cor: 'yellow', desc: '41-70 WCPM - Precisa de apoio sistemático', icon: Clock },
                  { nivel: 3, nome: 'Adequado', cor: 'blue', desc: '71-100 WCPM - Fluência adequada para o ano', icon: CheckCircle },
                  { nivel: 4, nome: 'Avançado', cor: 'green', desc: '101+ WCPM - Pode receber atividades de ampliação', icon: Award },
                ].map(({ nivel, nome, cor, desc, icon: Icon }) => {
                  const total = statsGerais.distribuicaoNiveis[
                    nivel === 1 ? 'critico' : 
                    nivel === 2 ? 'em_desenvolvimento' : 
                    nivel === 3 ? 'adequado' : 'avancado'
                  ];
                  const pct = statsGerais.totalAlunos > 0 
                    ? Math.round((total / statsGerais.totalAlunos) * 100) 
                    : 0;
                  
                  return (
                    <Card key={nivel} className={`border-l-4 border-${cor}-500`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 text-${cor}-600`} />
                            Nível {nivel} - {nome}
                          </CardTitle>
                          <Badge className={`bg-${cor}-100 text-${cor}-700`}>
                            {total} alunos ({pct}%)
                          </Badge>
                        </div>
                        <CardDescription>{desc}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-${cor}-500 transition-all duration-500`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {total > 0 && evolucaoData?.alunos && (
                          <div className="mt-3 max-h-32 overflow-y-auto">
                            <p className="text-xs text-muted-foreground mb-1">Alunos neste nível:</p>
                            <div className="flex flex-wrap gap-1">
                              {evolucaoData.alunos
                                .filter(a => a.nivel_atual === nivel)
                                .slice(0, 10)
                                .map(a => (
                                  <Badge key={a.student_id} variant="outline" className="text-xs">
                                    {a.student_name.split(' ')[0]}
                                  </Badge>
                                ))
                              }
                              {evolucaoData.alunos.filter(a => a.nivel_atual === nivel).length > 10 && (
                                <Badge variant="outline" className="text-xs">
                                  +{evolucaoData.alunos.filter(a => a.nivel_atual === nivel).length - 10} mais
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Tab Evolução Geral */}
            <TabsContent value="evolucao">
              <Card>
                <CardHeader>
                  <CardTitle>Evolução de Fluência</CardTitle>
                  <CardDescription>Acompanhe o progresso dos alunos ao longo do tempo</CardDescription>
                </CardHeader>
                <CardContent>
                  {!evolucaoData?.alunos || evolucaoData.alunos.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum dado de evolução disponível</p>
                      <p className="text-sm mt-2">Selecione uma turma ou registre mais avaliações</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aluno</TableHead>
                          <TableHead>Turma</TableHead>
                          <TableHead className="text-center">Avaliações</TableHead>
                          <TableHead className="text-center">WCPM Inicial</TableHead>
                          <TableHead className="text-center">WCPM Atual</TableHead>
                          <TableHead className="text-center">Evolução</TableHead>
                          <TableHead className="text-center">Nível Atual</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {evolucaoData.alunos.map(aluno => (
                          <TableRow key={aluno.student_id}>
                            <TableCell className="font-medium">{aluno.student_name}</TableCell>
                            <TableCell className="text-muted-foreground">{aluno.turma_name}</TableCell>
                            <TableCell className="text-center">{aluno.total_avaliacoes}</TableCell>
                            <TableCell className="text-center">{aluno.wcpm_inicial}</TableCell>
                            <TableCell className="text-center font-bold">{aluno.wcpm_atual}</TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold flex items-center justify-center gap-1 ${
                                aluno.evolucao > 0 ? 'text-green-600' : 
                                aluno.evolucao < 0 ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {aluno.evolucao > 0 ? <ChevronUp className="w-4 h-4" /> : 
                                 aluno.evolucao < 0 ? <ChevronDown className="w-4 h-4" /> : 
                                 <Minus className="w-4 h-4" />}
                                {aluno.evolucao > 0 ? '+' : ''}{aluno.evolucao}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={getNivelColor(aluno.nivel_atual)}>
                                {aluno.nivel_nome || getNivelNome(aluno.nivel_atual)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default RelatorioConsolidado;
